import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import { IContact, IContactMergeSummary, IMergePreview, IUnmergePreview } from "../models/Contact";
import { DefaultResponseWithData } from "../models/system";
import apiConfig from "../utils/apiConfig";

export interface IUnmergeRequest {
  mergeId: string;
  // Of the leads that reached the kept contact after the merge, the ones to send back.
  laterLeadIdsToMove: string[];
}

// A contact's merge history and the unmerge. The merges themselves go through useAccountDuplicates,
// which the Duplicates page shares. All admin-only on the API.
export default function useContactMerges(contactId?: string, enabled = true, componentName = "useContactMerges") {
  const { get, post } = useApiHelper(apiConfig(componentName));
  const queryClient = useQueryClient();

  const merges = useQuery({
    queryKey: ["contactMerges", contactId],
    queryFn: () => get<DefaultResponseWithData<IContactMergeSummary[]>>(`/contact/${contactId}/merges`),
    enabled: enabled && !!contactId,
    staleTime: 0,
  });

  const unmerge = useMutation({
    mutationFn: ({ mergeId, laterLeadIdsToMove }: IUnmergeRequest) =>
      post<object, DefaultResponseWithData<IContact>>(`/contact/merges/${mergeId}/unmerge`, { laterLeadIdsToMove }),
    onSuccess: () => {
      // Leads, documents and details move between two contacts.
      queryClient.invalidateQueries(["contactMerges"]);
      queryClient.invalidateQueries(["contactLeads"]);
      queryClient.invalidateQueries(["contact"]);
      queryClient.invalidateQueries(["contacts"]);
      queryClient.invalidateQueries(["contactDocuments"]);
      queryClient.invalidateQueries(["leadsDocuments"]);
      queryClient.invalidateQueries(["lead"]);
      queryClient.invalidateQueries(["leads"]);
      queryClient.invalidateQueries(["accounts"]);
    },
  });

  return { merges, unmerge };
}

// Both sides of a merge before anything moves. Built live, so never cached.
export function useMergePreview(keptId: string | null, sourceIds: string[], componentName = "useMergePreview") {
  const { get } = useApiHelper(apiConfig(componentName));
  const query = sourceIds.map((id) => `sourceIds=${id}`).join("&");

  return useQuery({
    queryKey: ["contactMerges", "preview", keptId, ...sourceIds],
    queryFn: () => get<DefaultResponseWithData<IMergePreview>>(`/contact/${keptId}/merge-preview?${query}`),
    enabled: !!keptId && sourceIds.length > 0,
    staleTime: 0,
    retry: false,
    // A refetch would reset the admin's choices in the dialog.
    refetchOnWindowFocus: false,
  });
}

export function useUnmergePreview(mergeId: string | null, componentName = "useUnmergePreview") {
  const { get } = useApiHelper(apiConfig(componentName));

  return useQuery({
    queryKey: ["contactMerges", "unmergePreview", mergeId],
    queryFn: () => get<DefaultResponseWithData<IUnmergePreview>>(`/contact/merges/${mergeId}/unmerge-preview`),
    enabled: !!mergeId,
    staleTime: 0,
    retry: false,
    // A refetch would reset the admin's choices in the dialog.
    refetchOnWindowFocus: false,
  });
}
